import { getDatabase, ref, get, set, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { LevelModel } from '../types/protocols';
import { requireAdmin } from './adminGuard';
import { recordActivity } from './activityApi';

/** Tombstone entity type for a level drawing (`structure/levels/{l}/blueprints/{k}`). */
export const LEVEL_BLUEPRINT_ENTITY_TYPE = 'levelBlueprint';
/** Tombstone entity type for the roof drawing (`structure/roofPlan/blueprints/{k}`). */
export const ROOF_PLAN_BLUEPRINT_ENTITY_TYPE = 'roofPlanBlueprint';

export interface LevelInput {
    name?: string;
    number: number;
    ceilingHeight?: number;
    knownArea?: number;
}

const stripUndefined = <T extends Record<string, unknown>>(value: T): T => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== undefined) result[key] = entry;
    }
    return result as T;
};

class LevelsApi {
    private db = getDatabase();

    private getCurrentUser() {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att utföra denna åtgärd');
        }
        return user;
    }

    private getLevelsPath(buildingKey: string): string {
        return `buildings/${buildingKey}/structure/levels`;
    }

    /** A building's levels without the rest of the building; raw nodes, like the building model's structure. */
    async getLevels(buildingKey: string): Promise<{ [levelKey: string]: LevelModel }> {
        const snapshot = await get(ref(this.db, this.getLevelsPath(buildingKey)));
        return snapshot.exists() ? (snapshot.val() as { [levelKey: string]: LevelModel }) : {};
    }

    /**
     * Update a level's number
     */
    async updateLevelNumber(
        buildingKey: string,
        levelKey: string,
        newNumber: number
    ): Promise<void> {
        this.getCurrentUser();

        const levelRef = ref(
            this.db,
            `buildings/${buildingKey}/structure/levels/${levelKey}/number`
        );
        await set(levelRef, newNumber);
        recordActivity('level.updated', { buildingKey, targetKey: levelKey });
    }

    /**
     * Create a level, mirroring iOS's "Redigera våning" create flow: a floor
     * number unique among the building's levels.
     */
    async createLevel(buildingKey: string, input: LevelInput): Promise<LevelModel> {
        await requireAdmin(this.db, 'skapa våningsplan');

        const snapshot = await get(ref(this.db, this.getLevelsPath(buildingKey)));
        const existing: LevelModel[] = snapshot.exists() ? Object.values(snapshot.val()) : [];

        if (!Number.isInteger(input.number)) {
            throw new Error('Våningsnummer måste vara ett heltal');
        }
        if (existing.some(l => l.number === input.number)) {
            throw new Error('Detta våningsnummer används redan av en annan våning');
        }

        const newRef = push(ref(this.db, this.getLevelsPath(buildingKey)));
        const key = newRef.key;
        if (!key) {
            throw new Error('Kunde inte skapa nyckel för våningsplanet');
        }

        const level: LevelModel = stripUndefined({
            key,
            name: input.name,
            number: input.number,
            ceilingHeight: input.ceilingHeight,
            knownArea: input.knownArea,
        });
        await set(ref(this.db, `${this.getLevelsPath(buildingKey)}/${key}`), level);
        recordActivity('level.created', { buildingKey, targetKey: key, label: level.name });
        return level;
    }
}

export const levelsApi = new LevelsApi();
